import { Link } from 'react-router-dom'
import { USERS } from '../mockData'
import { userDetailPath } from '../routes'
import RoleBadge from './RoleBadge'
import StatusPill from '../../../components/badges/StatusPill'
import { initials } from '../../../util/monogram'

export default function OrgUsersTable() {
    return (
        <div>
            {/* Role legend */}
            <p className="mb-3 text-[11.5px] text-gray-800">
                <span className="font-semibold text-gray-800">Admin</span> manage everything
                &middot;{' '}
                <span className="font-semibold text-gray-800">Member</span> moderate &amp; configure
                &middot;{' '}
                <span className="font-semibold text-gray-800">Viewer</span> read-only
            </p>

            <table className="w-full border-collapse text-sm">
                <thead>
                    <tr className="border-b border-gray-100 text-left">
                        <th
                            scope="col"
                            className="pb-2 pr-4 text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                        >
                            User
                        </th>
                        <th
                            scope="col"
                            className="pb-2 pr-4 text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                        >
                            Role
                        </th>
                        <th
                            scope="col"
                            className="pb-2 pr-4 text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                        >
                            Teams
                        </th>
                        <th
                            scope="col"
                            className="pb-2 pr-4 text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                        >
                            Status
                        </th>
                        <th
                            scope="col"
                            className="pb-2 pr-4 text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                        >
                            Last active
                        </th>
                        <th
                            scope="col"
                            className="pb-2 text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                        >
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                    {USERS.map((u) => {
                        const isInvited = u.status === 'Invited'
                        return (
                            <tr key={u.id} className="align-top">
                                {/* User cell */}
                                <td className="py-3 pr-4">
                                    <div className="flex items-center gap-2.5">
                                        {/* Avatar initials */}
                                        <div
                                            className={`flex h-8 w-8 flex-none items-center justify-center rounded-full text-xs font-bold ${
                                                isInvited
                                                    ? 'bg-gray-100 text-gray-400'
                                                    : 'bg-arwen-100 text-arwen-700'
                                            }`}
                                            aria-hidden="true"
                                        >
                                            {initials(u.name)}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-1.5">
                                                <Link
                                                    to={userDetailPath(u.id)}
                                                    className={`font-semibold hover:underline ${isInvited ? 'text-gray-400' : 'text-arwen-700'}`}
                                                >
                                                    {u.name}
                                                </Link>
                                                {u.isKeyContact && (
                                                    <span className="rounded-sm bg-arwen-50 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wide text-arwen-600">
                                                        Key Contact
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[11.5px] text-gray-800">
                                                {isInvited ? u.inviteNote : u.email}
                                            </div>
                                        </div>
                                    </div>
                                </td>

                                {/* Role cell */}
                                <td className="py-3 pr-4">
                                    <RoleBadge role={u.role} />
                                </td>

                                {/* Teams cell */}
                                <td className="py-3 pr-4">
                                    {u.allTeams ? (
                                        <span className="inline-flex items-center rounded bg-arwen-50 px-1.5 py-0.5 text-[11px] font-semibold text-arwen-700">
                                            All teams
                                        </span>
                                    ) : (
                                        <div className="flex flex-wrap gap-1">
                                            {u.teams.map((team) => (
                                                <span
                                                    key={team}
                                                    className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-800"
                                                >
                                                    {team}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </td>

                                {/* Status cell */}
                                <td className="py-3 pr-4">
                                    {isInvited ? (
                                        <StatusPill tone="suspect">Invited</StatusPill>
                                    ) : (
                                        <StatusPill tone="safe">Active</StatusPill>
                                    )}
                                </td>

                                {/* Last active cell */}
                                <td className="py-3 pr-4 text-[12.5px] text-gray-800">
                                    {u.lastActive}
                                </td>

                                {/* Actions cell */}
                                <td className="py-3">
                                    {isInvited ? (
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                aria-label={`Resend invitation to ${u.name}`}
                                                className="text-[12px] font-semibold text-arwen-600 hover:underline"
                                            >
                                                Resend
                                            </button>
                                            <span className="text-gray-300">·</span>
                                            <button
                                                type="button"
                                                aria-label={`Revoke invitation for ${u.name}`}
                                                className="text-[12px] font-semibold text-gray-800 hover:underline"
                                            >
                                                Revoke
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                aria-label={`Edit ${u.name}`}
                                                className="text-[12px] font-semibold text-gray-800 hover:underline"
                                            >
                                                Edit
                                            </button>
                                            <span className="text-gray-300">·</span>
                                            <button
                                                type="button"
                                                aria-label={`Remove ${u.name}`}
                                                className="text-[12px] font-semibold text-gray-800 hover:underline"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}
